import { useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Box, Button, Typography, IconButton } from '@mui/material';
import { Print as PrintIcon, Close as CloseIcon } from '@mui/icons-material';
import { ProfessionalReportPreviewProps, FeatureComparisonCell, FeatureComparisonRow } from './types';
import { AdviseSupplier, DEFAULT_PRICE_CATEGORIES, FeatureResponseState } from '../../../../types/advise';
import { formatCurrency } from '../../../../utils/helpers';
import { calcSupplierRoi, RoiInputs } from '../SideBySideView/RoiAnalysisPanel.calc';
import { roiMonthlySpend } from '../SideBySideView/currentMonthlyCxSpend.calc';
import { AWAITING_LABEL, AWAITING_MARK, STATE_LABELS, STATE_MARKS } from '../SideBySideView/SupplierFeatureInput.calc';
import { yearOneTotal } from '../pricing';
import { withheldReportSections } from '../EngagementSteps';

const STATE_COLORS: Record<FeatureResponseState, string> = {
  supported: '#10b981',
  partial: '#d97706',
  not_supported: '#ef4444'
};
const AWAITING_COLOR = '#9ca3af';

const markHTML = (state: FeatureResponseState | null) =>
  state
    ? `<span style="color:${STATE_COLORS[state]};">${STATE_MARKS[state]}</span>`
    : `<span style="color:${AWAITING_COLOR};">${AWAITING_MARK}</span>`;

// The PDF has no in-app legend, so the marks are spelled out under the table.
const FEATURE_KEY_HTML = (['supported', 'partial', 'not_supported', null] as const)
  .map((state) => `<span>${markHTML(state)} ${state ? STATE_LABELS[state] : AWAITING_LABEL}</span>`)
  .join('');

const generateReportHTML = (props: ProfessionalReportPreviewProps): string => {
  const {
    reportData: {
      customer,
      engagement,
      suppliers,
      date,
      executiveSummary,
      featureComparison,
      detailedPricing,
      roiAnalysis,
      settings,
      steps
    }
  } = props;

  // A turned-off step stays out of what the client receives, whatever the report's own settings say.
  const sections = { ...(settings?.sections || {}), ...withheldReportSections(steps) };
  const customerName = customer?.company_name || 'Customer';
  const engagementName = engagement.engagement_name || 'Supplier Comparison';

  const supplierHeaders = suppliers.map((v) => `<th>${v.supplier_name}</th>`).join('');

  // Feature comparison rows
  const featureRows = featureComparison
    .map((row: FeatureComparisonRow) => {
      const cells = suppliers
        .map((v: AdviseSupplier) => {
          const cell = row[v.id] as FeatureComparisonCell | undefined;
          if (!cell || typeof cell === 'string') {
            return '<td>—</td>';
          }
          const icon = markHTML(cell.state);
          const note = cell.note ? `<div class="feature-note">${cell.note}</div>` : '';
          return `<td>${icon}${note}</td>`;
        })
        .join('');
      return `<tr><td class="feature-name">${row.feature}</td>${cells}</tr>`;
    })
    .join('');

  // Pricing rows
  const pricingRows = DEFAULT_PRICE_CATEGORIES.map((category) => {
    const hasValues = suppliers.some(
      (v) =>
        (detailedPricing[v.id]?.[category]?.monthly || 0) > 0 || (detailedPricing[v.id]?.[category]?.oneTime || 0) > 0
    );
    if (!hasValues) return '';

    const cells = suppliers
      .map((v) => {
        const monthly = detailedPricing[v.id]?.[category]?.monthly || 0;
        const oneTime = detailedPricing[v.id]?.[category]?.oneTime || 0;
        const parts: string[] = [];
        if (monthly > 0) {
          parts.push(
            `<div>${formatCurrency(monthly)}` + '<span style="font-size:12px;color:#6b7280;">/mo</span></div>'
          );
        }
        if (oneTime > 0) {
          parts.push(
            `<div>${formatCurrency(oneTime)}` + '<span style="font-size:12px;color:#6b7280;"> one-time</span></div>'
          );
        }
        return `<td style="text-align:right;">${parts.join('') || '—'}</td>`;
      })
      .join('');
    return `<tr><td>${category}</td>${cells}</tr>`;
  }).join('');

  // Total row
  const totalCells = suppliers
    .map((v) => {
      const yearOne = yearOneTotal(detailedPricing[v.id] || {});
      return `<td style="text-align:right;font-weight:bold;">${formatCurrency(yearOne)}</td>`;
    })
    .join('');

  // ROI section — derive per-supplier ROI from the persisted inputs, same as RoiAnalysisPanel
  const roiInputs = (roiAnalysis || {}) as Partial<RoiInputs>;
  const currentMonthlySpend = roiMonthlySpend({ current_spend: engagement.current_spend, roi_analysis: roiAnalysis });
  const supplierRoi = suppliers.map((v) => {
    const roi = calcSupplierRoi(
      currentMonthlySpend,
      roiInputs.metrics || {},
      roiInputs.supplierProposals?.[v.id] || {}
    );
    return { supplier: v, roi, pct: Number.isFinite(roi.roiPct) ? Math.round(roi.roiPct) : 0 };
  });
  const hasRoi = supplierRoi.some(({ roi }) => roi.netYear1 !== 0 || roi.totalBenefit !== 0);

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>${engagementName} — ${customerName}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
@page { margin: 1in; size: letter; }
* { box-sizing: border-box; }
body {
  font-family: 'Inter', -apple-system, sans-serif;
  line-height: 1.6; color: #1a202c; margin: 0; padding: 0; background: white;
}
.header {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #06b6d4 100%);
  color: white; padding: 60px 40px 40px; page-break-after: always;
}
.header h1 { font-size: 36px; font-weight: 700; margin: 0 0 12px; }
.header .subtitle { font-size: 18px; opacity: 0.95; margin: 0 0 8px; }
.header .date { font-size: 14px; opacity: 0.8; }
.header .customer-name {
  background: white; color: #1e3a8a; padding: 20px 30px;
  border-radius: 8px; font-size: 24px; font-weight: 700;
  display: inline-block; margin-top: 20px;
}
.content { padding: 0 40px; }
.section { margin-bottom: 60px; page-break-before: always; }
.first-section { page-break-before: auto; }
.section-header { margin-bottom: 30px; padding-bottom: 12px; border-bottom: 3px solid #e5e7eb; }
.section-header h2 { color: #1e3a8a; font-size: 28px; font-weight: 600; margin: 0; }
.section-header .sub { color: #6b7280; font-size: 16px; margin: 8px 0 0; }
.summary-stats {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px;
  padding: 30px; background: white; border-radius: 8px; margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.stat-item { text-align: center; }
.stat-number { font-size: 32px; font-weight: 700; color: #1e3a8a; display: block; }
.stat-label { font-size: 14px; color: #6b7280; font-weight: 500; text-transform: uppercase; }
.exec-summary {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #cbd5e1; border-left: 6px solid #3b82f6;
  border-radius: 12px; padding: 40px; font-size: 16px; line-height: 1.7; color: #374151;
}
.feature-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.feature-table thead th {
  background: linear-gradient(135deg, #1e3a8a, #3b82f6);
  color: white; font-weight: 600; padding: 20px 15px; text-align: left;
}
.feature-table tbody td { padding: 18px 15px; border-bottom: 1px solid #f1f5f9; }
.feature-table tbody tr:nth-child(even) { background: #f8fafc; }
.feature-name { font-weight: 600; color: #1e3a8a; }
.feature-note {
  font-size: 12px; color: #6b7280; font-style: italic;
  margin-top: 4px; padding: 8px 12px; background: #f1f5f9; border-radius: 6px;
}
.feature-key { display: flex; flex-wrap: wrap; gap: 8px 24px; font-size: 12px; color: #6b7280; margin-top: 12px; }
.pricing-table { width: 100%; border-collapse: collapse; margin: 30px 0; }
.pricing-table thead th {
  background: #1e3a8a; color: white; font-weight: 600; padding: 20px 15px; text-align: right;
}
.pricing-table thead th:first-child { text-align: left; }
.pricing-table tbody td { padding: 15px; border-bottom: 1px solid #e5e7eb; text-align: right; }
.pricing-table tbody td:first-child { text-align: left; font-weight: 600; color: #374151; }
.total-row { background: #fef2f2; font-weight: 700; border-top: 2px solid #dc2626; }
.total-row td { color: #dc2626; }
.roi-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 25px; margin-top: 30px; }
.roi-card {
  background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 12px; padding: 25px;
}
.roi-card h4 { font-size: 16px; font-weight: 600; color: #1e3a8a; margin: 0 0 15px; }
.roi-card p { font-size: 28px; font-weight: 700; color: #1a202c; margin: 0; }
.roi-card .sub { font-size: 14px; color: #6b7280; }
.footer {
  margin-top: 80px; padding: 40px; background: #f8fafc;
  border-top: 3px solid #e5e7eb; text-align: center; border-radius: 12px;
}
.footer p { color: #6b7280; font-size: 14px; line-height: 1.6; }
@media print {
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; font-size: 12px; }
  .no-print { display: none !important; }
}
</style>
</head><body>
<div>
${
  sections.cover !== false
    ? `
<div class="header">
  <h1>Supplier Comparison Report</h1>
  <div class="subtitle">${engagementName}</div>
  <div class="date">Generated on ${date}</div>
  <div class="customer-name">${customerName}</div>
</div>`
    : ''
}

<div class="content">
${
  sections.cover !== false
    ? `
<div class="section first-section">
  <div class="section-header">
    <h2>Executive Summary</h2>
    <div class="sub">Comprehensive Supplier Evaluation Results</div>
  </div>
  <div class="summary-stats">
    <div class="stat-item">
      <span class="stat-number">${suppliers.length}</span>
      <div class="stat-label">Suppliers Evaluated</div>
    </div>
    <div class="stat-item">
      <span class="stat-number">${featureComparison.length}</span>
      <div class="stat-label">Requirements Analyzed</div>
    </div>
    <div class="stat-item">
      <span class="stat-number">${engagement.product_type || 'CX'}</span>
      <div class="stat-label">Product Type</div>
    </div>
  </div>
  ${executiveSummary ? `<div class="exec-summary">${executiveSummary}</div>` : ''}
</div>`
    : ''
}

${
  sections.feature_matrix !== false
    ? `
<div class="section">
  <div class="section-header">
    <h2>Feature Comparison Matrix</h2>
    <div class="sub">Detailed capability analysis</div>
  </div>
  <table class="feature-table">
    <thead><tr><th>Required Feature</th>${supplierHeaders}</tr></thead>
    <tbody>${featureRows}</tbody>
  </table>
  <div class="feature-key">${FEATURE_KEY_HTML}</div>
</div>`
    : ''
}

${
  sections.pricing !== false
    ? `
<div class="section">
  <div class="section-header">
    <h2>Investment Analysis</h2>
    <div class="sub">Cost comparison based on categorized line items</div>
  </div>
  <table class="pricing-table">
    <thead><tr><th>Cost Category</th>${suppliers.map((v) => `<th>${v.supplier_name}</th>`).join('')}</tr></thead>
    <tbody>
      ${pricingRows}
      <tr class="total-row">
        <td><strong>Total Year 1 Investment</strong></td>
        ${totalCells}
      </tr>
    </tbody>
  </table>
</div>`
    : ''
}

${
  sections.roi_analysis !== false && hasRoi
    ? `
<div class="section">
  <div class="section-header">
    <h2>Return on Investment Analysis</h2>
    <div class="sub">Projected business value</div>
  </div>
  <div class="roi-grid">
    ${supplierRoi
      .map(
        ({ supplier, roi, pct }) => `
    <div class="roi-card">
      <h4>${supplier.supplier_name}</h4>
      <p>${formatCurrency(roi.netYear1, { decimals: 0 })}</p>
      <span class="sub">Net Year 1 · ${pct}% ROI</span>
      <span class="sub">${formatCurrency(roi.annualSavings, { decimals: 0 })} annual savings</span>
    </div>`
      )
      .join('')}
  </div>
</div>`
    : ''
}

${
  sections.footer !== false
    ? `
<div class="footer">
  <p><strong>Confidential Report</strong> &bull; Generated on ${date} for ${customerName}</p>
  <p>Supplier Comparison &amp; Analysis &bull; Upstack Professional Consulting</p>
</div>`
    : ''
}
</div>
</div>
</body></html>`;
};

const FALLBACK_HTML = '<!DOCTYPE html><html><head></head><body><p>Unable to generate report preview.</p></body></html>';

export const ProfessionalReportPreview = ({ reportData, onClose }: ProfessionalReportPreviewProps) => {
  const htmlContent = useMemo(() => {
    try {
      return generateReportHTML({ reportData, onClose });
    } catch {
      return FALLBACK_HTML;
    }
  }, [reportData, onClose]);

  const handlePrint = useCallback(() => {
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ['html', 'head', 'body', 'meta', 'title', 'style', 'link'],
        ADD_ATTR: ['charset', 'class', 'style', 'href', 'rel']
      });
      printWindow.document.write(sanitizedHtml);
      printWindow.document.close();

      const imgs = Array.from(printWindow.document.images || []);
      const imgPromises = imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((res) => {
              img.addEventListener('load', () => res(), { once: true });
              img.addEventListener('error', () => res(), { once: true });
            })
      );
      Promise.all(imgPromises)
        .then(() => {
          printWindow.focus();
          printWindow.print();
        })
        .catch(() => printWindow.print());
    } catch {
      /* preview remains open; print simply unavailable for malformed data */
    }
  }, [htmlContent]);

  const bodyContent = useMemo(
    () => htmlContent.replace(/<!DOCTYPE html>[\s\S]*?<body>/i, '').replace(/<\/body>[\s\S]*$/i, ''),
    [htmlContent]
  );

  const styles = useMemo(() => {
    const styleMatch = htmlContent.match(/<style>([\s\S]*?)<\/style>/i);
    return styleMatch ? styleMatch[1] : '';
  }, [htmlContent]);

  return (
    <Box className="flex flex-col h-full bg-white">
      <Box className="flex items-center justify-between p-4 border-b bg-gray-50">
        <Box>
          <Typography variant="h6">Professional Report Preview</Typography>
          <Typography
            variant="body2"
            color="textSecondary">
            {reportData.date}
          </Typography>
        </Box>
        <Box className="flex gap-2">
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}>
            Print / PDF
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box className="flex-1 overflow-auto bg-gray-100 p-4">
        <Box
          className="bg-white shadow-lg mx-auto"
          sx={{ maxWidth: '8.5in', minHeight: '11in' }}>
          <style dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(styles, { FORCE_BODY: true }) }} />
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bodyContent, { ADD_TAGS: ['style'], ADD_ATTR: ['class', 'style'] })
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
